// ===============================
// src/components/AllOffersProviderLinesRowSeven.jsx
// (טאב "הצעות חדשות" – סטטוס 1)
// ✅ עודכן: שדות/עמודות כמו AllOffersAdminLines + עמודת פעולה לאישור
// ===============================

import { Text, Img, Heading, Button } from "./..";
import React, { useEffect, useMemo, useState } from "react";
import { API_DOMAIN } from "config";
import axios from "axios";
import { Switch } from "../../components";
import { formatPrice } from "utils/formatPrice";
import { hasActiveJ5Hold } from "utils/j5Status";
import ApproveOfferByProviderPopup from "components/ApproveOfferByProviderPopup";
const COLS = [
  { key: "name",        title: "שם",          w: "14%" },
  { key: "contact",     title: "איש קשר",     w: "13%" },
  { key: "offerPrice",  title: "מחיר הצעה",   w: "11%" },
  { key: "askingPrice", title: "מחיר מבוקש",  w: "11%" },
  { key: "date",        title: "מועד ההצעה",  w: "10%" },
  { key: "city",        title: "מיקום",       w: "8%"  },
  { key: "reserved",    title: "שוריין / לא", w: "10%" },
  { key: "pickup",      title: "איסוף",       w: "9%"  },
  { key: "payEnd",      title: "סיום תשלום",  w: "9%"  },
  { key: "action",      title: "אישור ההצעה", w: "14%" },
];

export default function AllOffersProviderLinesRowSeven({
  car,
  rows,
  onOfferApproved,
  updateShowApprovedOfferPopup,
  setSelectedOffer,
  setSelectedCar,
}) {
  const [showApprovedOfferPopuop, setShowApprovedOfferPopup] = useState(false);

  const template = COLS.map((c) => c.w).join(" ");
  const isAuctionCar = car?.is_auction === true || Number(car?.is_auction ?? car?.isAuction ?? 0) === 1;

  const handlePopupClose = () => {
    setShowApprovedOfferPopup(false);
  };

  const approveOffer = async (offer) => {
    if (isAuctionCar) return;

    try {
      await axios.post(`${API_DOMAIN}/offer/approveOfferByProvider`, {
        offer_id: Number(offer.id),
      });

      onOfferApproved?.(car?.id, offer?.id);

      setSelectedOffer?.(offer);
      setSelectedCar?.(car);

      updateShowApprovedOfferPopup?.(true);
    } catch (err) {
      console.error(err);
      alert("אירעה שגיאה באישור ההצעה");
    }
  };

  const hasValue = (v) =>
    v !== null && v !== undefined && String(v).trim() !== "";

  // נרמול שדות (תומך גם snake_case מהשרת וגם camelCase)
  const getBuyerName = (r) => r?.buyerName ?? r?.buyer_name ?? "";
  const getBuyerContactName = (r) =>
    r?.buyerContactName ?? r?.buyer_contact_name ?? r?.contact_name ?? "";
  const getBuyerPhone = (r) =>
    r?.buyerPhoneNumber ?? r?.buyer_phone_number ?? r?.phone_number ?? "";

  const getProviderName = (r) =>
    r?.providerName ?? r?.provider_name ?? car?.provider_name ?? "";

  // פורמט תאריך DD/MM/YY
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date)) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  /* ========================= SORTING ========================= */
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const sortAsc = (key) => {
    setSortKey(key);
    setSortDir("asc");
  };
  const sortDesc = (key) => {
    setSortKey(key);
    setSortDir("desc");
  };

  const normalizeString = (v) => (v == null ? "" : String(v).trim().toLowerCase());

  const parseNumber = (v) => {
    if (v == null) return null;
    const s = String(v).replace(/[^\d.-]/g, "");
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const parseDateValue = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d) ? null : d.getTime();
  };

  const compareValues = (a, b) => {
    const aEmpty = a === null || a === undefined || a === "";
    const bEmpty = b === null || b === undefined || b === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (typeof a === "number" && typeof b === "number") return a - b;

    return String(a).localeCompare(String(b), "he", {
      numeric: true,
      sensitivity: "base",
    });
  };

  const getSortValue = (r, key) => {
    switch (key) {
      case "name":
        return normalizeString(getBuyerName(r));

      case "contact":
        return normalizeString(getBuyerContactName(r) || getBuyerPhone(r));

      case "provider":
        return normalizeString(getProviderName(r));

      case "offerPrice":
        return parseNumber(r.offerPrice ?? r.price ?? r.offered_price) ?? 0;

      case "askingPrice":
        return parseNumber(r.carPrice ?? car?.price ?? car?.asking_price) ?? 0;

      case "date":
        return parseDateValue(r.offerDate ?? r.creation_date);

      case "city":
        return normalizeString(r.offerPosition);

      case "reserved":
        return hasActiveJ5Hold(r) ? 1 : 0;

      case "pickup":
        return parseDateValue(r.collectionDate ?? r.collection_date);

      case "payEnd":
        return parseDateValue(r.lastPaymentDate ?? r.last_payment_date);

      // action לא ממיינים בפועל
      case "action":
      default:
        return "";
    }
  };

  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    if (!sortKey) return list;

    list.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      const diff = compareValues(va, vb);
      return sortDir === "asc" ? diff : -diff;
    });

    return list;
  }, [rows, sortKey, sortDir]);

  const ArrowButtons = ({ colKey }) => (
    <div
      className="flex flex-col items-center leading-none"
      style={{ width: "8px", gap: "2px" }}
    >
      <button
        type="button"
        onClick={() => sortAsc(colKey)}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          lineHeight: 0,
          fontSize: 0,
        }}
      >
        <img
          src="/images/img_vector_red_a700_03_9.svg"
          alt="up-arrow"
          style={{
            display: "block",
            opacity: sortKey === colKey && sortDir === "asc" ? 1 : 0.45,
          }}
        />
      </button>

      <button
        type="button"
        onClick={() => sortDesc(colKey)}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          lineHeight: 0,
          fontSize: 0,
        }}
      >
        <img
          src="/images/img_vector_red_a700_03_10.svg"
          alt="down-arrow"
          style={{
            display: "block",
            opacity: sortKey === colKey && sortDir === "desc" ? 1 : 0.45,
          }}
        />
      </button>
    </div>
  );

  const bgUrl = car?.images?.[0]?.image_url
    ? `${API_DOMAIN}/${car.images[0].image_url}`
    : "/images/img_car11_qgcqjcnfp_428x738.png";

  return (
    <div>
      <div className="mt-2 flex w-full flex-col gap-2.5 self-end">
               {isAuctionCar ? (
                <span 
className="ml-auto inline-flex shrink-0 items-center justify-center rounded-[14px] border border-orange-300 !bg-orange-500 px-3 py-1 mr-1 text-[13px] font-bold !text-white !w-[100px]"                style={{color: 'white'}}>
                  רכב במכרז
                </span>
              ) : null}
               <div className="flex items-end md:ml-0 md:flex-col">
          <div></div>

          {/* כותרת */}
        
          <div className="mt-3.5 flex flex-1 items-start justify-center gap-3.5 px-3.5 md:self-stretch">
            <div className="flex flex-1 items-center justify-end">
              <Text size="text4xl" as="p" className="text-[22px] font-normal lg:text-[18px]">
                {car?.year}
              </Text>
              <div className="ml-5 h-[8px] w-[10px] rounded bg-red-a700_03" />
              <Text size="text4xl" as="p" className="ml-[18px] text-[22px] font-normal lg:text-[18px]">
              {car?.model} - {car?.manufacturer}
              </Text>
        
            </div>

          </div>
        </div>

        <div className="flex items-start gap-[38px] md:ml-0 md:flex-col">
          <div className="wrapper-line">
            <div className="flex justify-center md:mx-0 md:flex-col">
              <div dir="rtl" className="w-full overflow-x-auto">
                {/* HEADER */}
                <div
                  className="grid items-center bg-white sticky top-0 z-10"
                  style={{ gridTemplateColumns: template }}
                >
                  {COLS.map((c) => (
                    <div
                      key={c.key}
                      className="flex items-center justify-center py-2 text-sm font-assistant font-bold relative gap-2"
                    >
                      <span>{c.title}</span>
                      {c.key !== "action" && <ArrowButtons colKey={c.key} />}
                    </div>
                  ))}
                </div>

                {/* BODY */}
                {sortedRows.map((r, i) => {
                  const askingPriceVal = r.carPrice ?? car?.price ?? car?.asking_price;
                  const offerPriceVal = r.offerPrice ?? r.price ?? r.offered_price;

                  const buyerName = getBuyerName(r);
                  const buyerContactName = getBuyerContactName(r);
                  const buyerPhone = getBuyerPhone(r);
                  const providerName = getProviderName(r);

                  return (
                    <div
                      key={r?.id ?? i}
                      style={{
                        height: "4rem",
                        marginBottom: "0.5rem",
                        marginTop: "0.5rem",
                        gridTemplateColumns: template,
                      }}
                      className="grid border-none gap-0 items-center bg-gray-50 hover:bg-gray-100 text-[15px]"
                    >
                      {/* שם */}
                      <span className="text-center font-semibold relative">
                        {hasValue(buyerName) ? buyerName : "—"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* איש קשר: שם וטלפון */}
                      <span className="text-center font-semibold relative leading-tight">
                        <div className="text-[12px] font-normal text-gray-800">
                          {hasValue(buyerContactName) ? buyerContactName : "—"}
                        </div>
                        <div className="text-[12px] font-normal text-gray-600">
                          {hasValue(buyerPhone) ? (
                            <a href={`tel:${buyerPhone}`} className="hover:underline">
                              {buyerPhone}
                            </a>
                          ) : (
                            "—"
                          )}
                        </div>
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>


                  
                      {/* מחיר הצעה */}
                      <span className="text-center font-bold text-red-a700_03 relative">
                        {hasValue(offerPriceVal) ? `₪${formatPrice(offerPriceVal)}` : "—"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* מחיר מבוקש */}
                      <span className="text-center font-bold relative">
                        {hasValue(askingPriceVal) ? `₪${formatPrice(askingPriceVal)}` : "—"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* מועד ההצעה */}
                      <span className="text-center relative">
                        {formatDate(r.offerDate ?? r.creation_date)}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* מיקום */}
                      <span className="text-center relative">
                        {r.offerPosition ?? "—"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* שוריין / לא */}
                      <span className="flex justify-center items-center relative">
                        <Switch checked={hasActiveJ5Hold(r)} disabled={true} onCheckedChange={() => {}} />
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* איסוף */}
                      <span className="text-center relative">
                        {formatDate(r.collectionDate ?? r.collection_date)}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* סיום תשלום */}
                      <span className="text-center relative">
                        {formatDate(r.lastPaymentDate ?? r.last_payment_date)}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* אישור ההצעה */}
                      <span className="flex justify-center items-center">
                        <Button
                          size="lg"
                          shape="round"
                          disabled={isAuctionCar}
                          className="flex items-center justify-center gap-2 rounded-[16px] border border-solid border-light_green-800 px-3 py-2 font-semibold text-light_green-800 w-full md:w-auto md:self-stretch"
                          style={isAuctionCar ? { display: "none" } : undefined}
                          onClick={() => {
                            setSelectedCar?.(car);
                            setSelectedOffer?.(r);
                            approveOffer(r);
                          }}
                        >
                          <span className="flex-1 text-center text-[14px] md:text-[10px] lg:text-[13px]">
                            אישור ההצעה
                          </span>
                          <Img
                            src="/images/img_arrowleft_light_green_800_2.svg"
                            alt="Arrow"
                            className="h-[12px] w-[8px] md:h-[14px] md:w-[10px] ml-2"
                          />
                        </Button>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* תמונה */}
          <div className="mt-5 flex w-[20%] justify-center rounded-md md:w-full">
            <div className="w-full rounded-md">
              <div
                className="flex h-[184px] items-start rounded-[10px] bg-cover bg-no-repeat px-6 py-[22px] lg:h-auto md:h-auto sm:p-4"
                style={{
                  height: "21vh",
                  objectFit: "contain",
                  backgroundImage: `url(${bgUrl})`,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* אם את עדיין משתמשת בפופאפ מקומי */}
      {showApprovedOfferPopuop ? (
        <ApproveOfferByProviderPopup onClose={handlePopupClose} />
      ) : null}
    </div>
  );
}
